/**
 * The String and Tallies tabs of a well servicing job (fieldiq-api#1286, WS-3).
 *
 * STRING EVENTS ARE APPEND-ONLY, and this screen offers exactly the doors the
 * API keeps open — nothing here edits what an event says:
 *   - on a DRAFT sheet the tour roles add events, and may remove one that is
 *     not a correction (it is not yet a record);
 *   - on a SUBMITTED or APPROVED sheet only the office (admin, office staff)
 *     adds, and every add is a CORRECTION that needs a reason. "Correct" on an
 *     event pre-names it, so the API voids it in the same transaction;
 *   - the office VOIDS any live event, with a reason.
 * A correction with no reason is refused HERE as well as by the API: it is the
 * one write that must never go out looking like an ordinary event.
 *
 * TALLIES are a joint-by-joint count on a sheet. The TOTALS (joints, total,
 * average, shortest, longest, by condition) are the API's, shown as sent —
 * nothing here adds the lines up. A tally changes only while its sheet is a
 * draft (the API's `locked`); after that the office voids it with a reason.
 *
 * Absent values render `—`, never 0; units are the ones the API sent.
 */
import React, { useMemo, useState } from 'react';
import { Ban, Pencil, Plus, Trash2, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { useWsTours } from '../../hooks/useWsJobs';
import {
  useAddWsStringEvent,
  useCreateWsTally,
  useRemoveWsStringEvent,
  useRemoveWsTally,
  useUpdateWsTally,
  useVoidWsStringEvent,
  useVoidWsTally,
  useWsJobStringEvents,
  useWsTallies,
} from '../../hooks/useWsString';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canApproveWsTours,
  canWriteWsTours,
  catalogLabel,
  emptyWsEventForm,
  fmtWsNumber,
  withUnit,
  wsDay,
  wsDepthRange,
  wsEntered,
  wsEventBody,
  wsEventRemovable,
  wsEventVoidable,
  wsItemIdentity,
  wsJobRefusal,
  wsStringAddMode,
  wsTallyLinesBody,
  wsTourStatusSkin,
  WS_CORRECTION_SKIN,
  WS_VOIDED_SKIN,
  type WsEventForm,
  type WsTallyLineForm,
} from '../../lib/well-servicing';
import type { WsCatalog, WsJobDetail, WsStringEvent, WsTally, WsTourSummary } from '../../types/well-servicing';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  WS_INPUT,
  WS_PRIMARY,
  WS_SECONDARY,
  WsBadge,
  WsDialog,
  WsEmpty,
  WsField,
  WsFormError,
  WsLoading,
  WsPanel,
  WsSpec,
} from './WsBits';

// ─── Shared pieces ───────────────────────────────────────────────────────────

/** A void (event or tally): the reason is required before anything is sent. */
export function VoidDialog({
  open, title, body, pending, error, onClose, onVoid,
}: {
  open: boolean; title: string; body: string; pending: boolean; error: string | null;
  onClose: () => void; onVoid: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [local, setLocal] = useState<string | null>(null);
  React.useEffect(() => {
    if (open) {
      setReason('');
      setLocal(null);
    }
  }, [open]);
  return (
    <WsDialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WS_SECONDARY} onClick={onClose} disabled={pending}>
            Keep it
          </button>
          <button
            type="button"
            className={WS_PRIMARY}
            disabled={pending}
            data-testid="ws-void-confirm"
            onClick={() => {
              setLocal(null);
              if (!reason.trim()) return setLocal('Say why it is being voided.');
              onVoid(reason.trim());
            }}
          >
            {pending ? 'Voiding…' : 'Void'}
          </button>
        </>
      }
    >
      <WsFormError message={local ?? error} />
      <p className="mb-3 text-sm text-fieldiq-text">{body}</p>
      <WsField label="Reason">
        <textarea className={`${WS_INPUT} h-20 py-2`} aria-label="Void reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      </WsField>
    </WsDialog>
  );
}

function Confirm({
  open, title, body, confirmLabel, pending, error, onClose, onConfirm,
}: {
  open: boolean; title: string; body: string; confirmLabel: string; pending: boolean; error: string | null;
  onClose: () => void; onConfirm: () => void;
}) {
  return (
    <WsDialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WS_SECONDARY} onClick={onClose} disabled={pending}>
            Keep it
          </button>
          <button type="button" className={WS_PRIMARY} onClick={onConfirm} disabled={pending}>
            {pending ? 'Removing…' : confirmLabel}
          </button>
        </>
      }
    >
      <WsFormError message={error} />
      <p className="text-sm text-fieldiq-text">{body}</p>
    </WsDialog>
  );
}

const opts = (list: { value: string; label: string }[] | undefined) =>
  (list ?? []).map((o) => (
    <option key={o.value} value={o.value}>
      {o.label}
    </option>
  ));

/**
 * The string-event form. Shared by the office and the field screen; `mode`
 * decides whether the correction reason is asked for (and required).
 */
export function StringEventForm({
  catalog, depthUnits, initial, mode, pending, error, submitLabel, onCancel, onSubmit, correcting,
}: {
  catalog: WsCatalog | null;
  depthUnits: string[];
  initial: WsEventForm;
  mode: 'draft' | 'correction';
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (f: WsEventForm) => void;
  /** The event this correction replaces, as a sentence. */
  correcting?: string | null;
}) {
  const [f, setF] = useState<WsEventForm>(initial);
  const set = (k: keyof WsEventForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setF((cur) => ({ ...cur, [k]: e.target.value }));
  return (
    <form
      className="space-y-3"
      data-testid="ws-string-event-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(f);
      }}
    >
      <WsFormError message={error} />
      {mode === 'correction' && (
        <p className="rounded-fi-control border border-fi-amber/60 bg-fi-amber/10 px-3 py-2 text-sm text-fieldiq-text" data-testid="ws-correction-notice">
          This sheet is submitted, so this event is recorded as a correction.
          {correcting ? ` It replaces ${correcting}, which is voided with your reason.` : ''}
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <WsField label="What happened">
          <select className={WS_INPUT} aria-label="Action" value={f.action} onChange={set('action')}>
            <option value="">Choose…</option>
            {opts(catalog?.stringActions)}
          </select>
        </WsField>
        <WsField label="Component">
          <select className={WS_INPUT} aria-label="Component" value={f.componentKind} onChange={set('componentKind')}>
            <option value="">Choose…</option>
            {opts(catalog?.componentKinds)}
          </select>
        </WsField>
        <WsField label="Quantity">
          <input className={WS_INPUT} inputMode="numeric" aria-label="Quantity" value={f.quantity} onChange={set('quantity')} />
        </WsField>
        <WsField label="Unit" hint="For the length and depths">
          <select className={WS_INPUT} aria-label="Length unit" value={f.unit} onChange={set('unit')}>
            {depthUnits.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </WsField>
        <WsField label="Description">
          <input className={WS_INPUT} aria-label="Description" value={f.description} onChange={set('description')} />
        </WsField>
        <WsField label="Size">
          <input className={WS_INPUT} aria-label="Size" value={f.size} onChange={set('size')} />
        </WsField>
        <WsField label="Grade">
          <input className={WS_INPUT} aria-label="Grade" value={f.grade} onChange={set('grade')} />
        </WsField>
        <WsField label="Serial number">
          <input className={WS_INPUT} aria-label="Serial number" value={f.serialNumber} onChange={set('serialNumber')} />
        </WsField>
        <WsField label="Length" hint="Blank if not measured">
          <input className={WS_INPUT} inputMode="decimal" aria-label="Length" value={f.length} onChange={set('length')} />
        </WsField>
        <WsField label="Top depth">
          <input className={WS_INPUT} inputMode="decimal" aria-label="Top depth" value={f.topDepth} onChange={set('topDepth')} />
        </WsField>
        <WsField label="Bottom depth">
          <input className={WS_INPUT} inputMode="decimal" aria-label="Bottom depth" value={f.bottomDepth} onChange={set('bottomDepth')} />
        </WsField>
        <WsField label="Condition">
          <select className={WS_INPUT} aria-label="Condition" value={f.condition} onChange={set('condition')}>
            <option value="">Not stated</option>
            {opts(catalog?.stringConditions)}
          </select>
        </WsField>
        <WsField label="Owner">
          <select className={WS_INPUT} aria-label="Owner" value={f.owner} onChange={set('owner')}>
            {opts(catalog?.stringOwners)}
          </select>
        </WsField>
      </div>
      <WsField label="Notes">
        <textarea className={`${WS_INPUT} h-16 py-2`} aria-label="Event notes" value={f.notes} onChange={set('notes')} />
      </WsField>
      {mode === 'correction' && (
        <WsField label="Why it is being corrected" hint="Required on a submitted sheet">
          <textarea className={`${WS_INPUT} h-16 py-2`} aria-label="Correction reason" value={f.correctionReason} onChange={set('correctionReason')} />
        </WsField>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WS_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={WS_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

/** `Ran in · Tubing` from the API's labels (raw value when the label is missing). */
const eventWhat = (e: WsStringEvent, catalog: WsCatalog | null) =>
  `${e.actionLabel ?? catalogLabel(catalog?.stringActions, e.action)} · ${e.componentKindLabel ?? catalogLabel(catalog?.componentKinds, e.componentKind)}`;

// ─── String ──────────────────────────────────────────────────────────────────

type Adding = { tour: WsTourSummary; mode: 'draft' | 'correction'; corrects: WsStringEvent | null };

export function StringSection({
  job, catalog, depthUnit, role,
}: {
  job: WsJobDetail;
  catalog: WsCatalog | null;
  depthUnit: string;
  role: string | null | undefined;
}) {
  const tours = useWsTours(job.id);
  const events = useWsJobStringEvents(job.id);
  const add = useAddWsStringEvent();
  const remove = useRemoveWsStringEvent();
  const voidEvent = useVoidWsStringEvent();
  const [adding, setAdding] = useState<Adding | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [voiding, setVoiding] = useState<WsStringEvent | null>(null);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<WsStringEvent | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const depthUnits = catalog?.depthUnits?.length ? catalog.depthUnits : ['m', 'ft'];

  const byTour = useMemo(() => {
    const m = new Map<string, WsStringEvent[]>();
    for (const e of events.data?.data ?? []) {
      const list = m.get(e.tourId) ?? [];
      list.push(e);
      m.set(e.tourId, list);
    }
    return m;
  }, [events.data]);
  const sheets = [...(tours.data?.data ?? [])].sort((a, b) => (a.workDate < b.workDate ? 1 : a.workDate > b.workDate ? -1 : 0));
  const loading = tours.isLoading || events.isLoading;
  const failQuery = queryLoadFailure(tours) ? tours : events;
  const failure = queryLoadFailure(tours) ?? queryLoadFailure(events);

  const startAdd = (tour: WsTourSummary, corrects: WsStringEvent | null = null) => {
    const mode = wsStringAddMode(tour.status, role);
    if (!mode) return;
    setError(null);
    setAdding({ tour, mode, corrects });
  };

  const initialFor = (a: Adding): WsEventForm => {
    const base = emptyWsEventForm(depthUnit);
    const c = a.corrects;
    if (!c) return base;
    return {
      ...base,
      action: c.action,
      componentKind: c.componentKind,
      description: c.description ?? '',
      size: c.size ?? '',
      grade: c.grade ?? '',
      serialNumber: c.serialNumber ?? '',
      quantity: String(c.quantity),
      length: c.length ? String(c.length.value) : '',
      topDepth: c.topDepth ? String(c.topDepth.value) : '',
      bottomDepth: c.bottomDepth ? String(c.bottomDepth.value) : '',
      unit: c.length?.unit ?? c.depthUnit ?? depthUnit,
      condition: c.condition ?? '',
      owner: c.owner,
      notes: c.notes ?? '',
      correctsEventId: c.id,
    };
  };

  const submit = (f: WsEventForm) => {
    if (!adding) return;
    setError(null);
    const built = wsEventBody(f, adding.mode);
    if ('error' in built) return setError(built.error);
    add.mutate(
      { tourId: adding.tour.id, body: built.body },
      {
        onSuccess: () => {
          toast.success(adding.mode === 'correction' ? 'Correction recorded' : 'String event added');
          setAdding(null);
        },
        onError: (err) => setError(wsJobRefusal(err, 'The event could not be added.')),
      },
    );
  };

  return (
    <WsPanel eyebrow="String" title="String events" testId="ws-job-string">
      <p className="border-b border-fieldiq-border px-4 py-3 text-sm text-fieldiq-muted">
        What was pulled, run, laid down, sent for repair or inspected, sheet by sheet. Events are never edited: a draft sheet&apos;s event can be
        removed, and once a sheet is submitted the office records a correction or voids an event, each with a reason.
      </p>
      {loading ? (
        <WsLoading label="Loading the string events" />
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(failQuery)} kind={failure} subject="the string events" />
      ) : sheets.length === 0 ? (
        <WsEmpty>No tour sheet has been opened on this job yet. String events are recorded on a tour sheet.</WsEmpty>
      ) : (
        <div className="divide-y divide-fieldiq-border">
          {sheets.map((t) => {
            const list = byTour.get(t.id) ?? [];
            const mode = wsStringAddMode(t.status, role);
            const open = adding?.tour.id === t.id;
            return (
              <section key={t.id} className="space-y-3 px-4 py-4" data-testid="ws-string-sheet" data-tour-status={t.status}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-extrabold text-white">{wsDay(t.workDate)}</h3>
                    <WsBadge skin={wsTourStatusSkin(t.status, t.statusLabel)} value={t.status} />
                    <span className="text-xs text-fieldiq-muted">
                      {list.length} {list.length === 1 ? 'event' : 'events'}
                    </span>
                  </div>
                  {mode && !open && (
                    <button type="button" className={WS_PRIMARY} onClick={() => startAdd(t)} data-testid="ws-add-string-event">
                      <Plus className="size-4" aria-hidden="true" />
                      {mode === 'correction' ? 'Add correction' : 'Add event'}
                    </button>
                  )}
                </div>
                {open && adding && (
                  <div className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-3">
                    <StringEventForm
                      key={`${adding.tour.id}-${adding.corrects?.id ?? 'new'}`}
                      catalog={catalog}
                      depthUnits={depthUnits}
                      initial={initialFor(adding)}
                      mode={adding.mode}
                      pending={add.isPending}
                      error={error}
                      submitLabel={adding.mode === 'correction' ? 'Record correction' : 'Add event'}
                      correcting={adding.corrects ? `event ${adding.corrects.seq} (${eventWhat(adding.corrects, catalog)})` : null}
                      onCancel={() => setAdding(null)}
                      onSubmit={submit}
                    />
                  </div>
                )}
                {list.length === 0 ? (
                  <WsEmpty>No string event on this sheet.</WsEmpty>
                ) : (
                  <ul className="space-y-2">
                    {list.map((e) => (
                      <li
                        key={e.id}
                        data-testid="ws-string-event"
                        data-voided={e.voided ? 'true' : 'false'}
                        className={`rounded-fi-control border border-fieldiq-border border-l-4 bg-fieldiq-card px-3 py-2 ${
                          e.voided ? WS_VOIDED_SKIN.rail : e.correctsEventId ? WS_CORRECTION_SKIN.rail : 'border-l-transparent'
                        }`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className={`text-sm font-extrabold ${e.voided ? 'text-fieldiq-muted line-through' : 'text-white'}`}>
                              {e.seq}. {eventWhat(e, catalog)} × {fmtWsNumber(e.quantity)}
                            </p>
                            <p className="mt-0.5 text-xs text-fieldiq-text">
                              {wsItemIdentity(e)} · Length {wsEntered(e.length)} · Depth {wsDepthRange(e.topDepth, e.bottomDepth)} · Condition{' '}
                              {e.conditionLabel ?? (e.condition ? catalogLabel(catalog?.stringConditions, e.condition) : '—')} · Owner{' '}
                              {e.ownerLabel ?? catalogLabel(catalog?.stringOwners, e.owner)}
                              {e.equipmentName ? ` · ${e.equipmentName}` : ''}
                            </p>
                            {e.notes && <p className="mt-0.5 whitespace-pre-wrap text-xs text-fieldiq-muted">{e.notes}</p>}
                            {e.voided && (
                              <p className="mt-0.5 text-xs text-fi-red" data-testid="ws-event-void-reason">
                                Voided{e.voidedByName ? ` by ${e.voidedByName}` : ''}: {e.voidReason || '—'}
                              </p>
                            )}
                            <p className="mt-0.5 text-[0.7rem] text-fieldiq-muted">Recorded by {e.createdByName || '—'}</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {e.voided && <WsBadge skin={WS_VOIDED_SKIN} value="voided" />}
                            {e.correctsEventId && <WsBadge skin={WS_CORRECTION_SKIN} value="correction" />}
                            {mode === 'correction' && !e.voided && (
                              <button type="button" className={WS_SECONDARY} onClick={() => startAdd(t, e)} aria-label={`Correct event ${e.seq}`}>
                                <Wrench className="size-4" aria-hidden="true" />
                                Correct
                              </button>
                            )}
                            {wsEventVoidable(e, role) && (
                              <button type="button" className={WS_SECONDARY} onClick={() => { setVoidError(null); setVoiding(e); }} aria-label={`Void event ${e.seq}`}>
                                <Ban className="size-4" aria-hidden="true" />
                                Void
                              </button>
                            )}
                            {wsEventRemovable(e, role) && (
                              <button type="button" className={WS_SECONDARY} onClick={() => { setRemoveError(null); setRemoving(e); }} aria-label={`Remove event ${e.seq}`}>
                                <Trash2 className="size-4" aria-hidden="true" />
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}
      <VoidDialog
        open={!!voiding}
        title="Void event"
        body={voiding ? `Voids event ${voiding.seq} (${eventWhat(voiding, catalog)}). It stays on the record, struck through, with your reason.` : ''}
        pending={voidEvent.isPending}
        error={voidError}
        onClose={() => setVoiding(null)}
        onVoid={(reason) =>
          voiding &&
          voidEvent.mutate(
            { id: voiding.id, reason },
            {
              onSuccess: () => {
                toast.success('Event voided');
                setVoiding(null);
              },
              onError: (err) => setVoidError(wsJobRefusal(err, 'The event could not be voided.')),
            },
          )
        }
      />
      <Confirm
        open={!!removing}
        title="Remove event"
        body={removing ? `Removes event ${removing.seq} (${eventWhat(removing, catalog)}) from this draft sheet.` : ''}
        confirmLabel="Remove"
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => {
              toast.success('Event removed');
              setRemoving(null);
            },
            onError: (err) => setRemoveError(wsJobRefusal(err, 'The event could not be removed.')),
          })
        }
      />
    </WsPanel>
  );
}

// ─── Tallies ─────────────────────────────────────────────────────────────────

const blankLine = (): WsTallyLineForm => ({ length: '', serialNumber: '', condition: '' });

/** The joints, one row each. Shared by the office and the field screen. */
export function TallyLinesEditor({
  rows, onChange, catalog, unit,
}: {
  rows: WsTallyLineForm[];
  onChange: (rows: WsTallyLineForm[]) => void;
  catalog: WsCatalog | null;
  unit: string;
}) {
  const set = (i: number, k: keyof WsTallyLineForm, v: string) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid="ws-tally-lines-editor">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[3rem_1fr_1fr_1fr_auto] items-end gap-2">
          <span className="pb-2 text-right font-mono text-sm text-fieldiq-muted">{i + 1}</span>
          <input className={WS_INPUT} inputMode="decimal" aria-label={`Joint ${i + 1} length (${unit})`} placeholder={`Length (${unit})`} value={r.length} onChange={(e) => set(i, 'length', e.target.value)} />
          <input className={WS_INPUT} aria-label={`Joint ${i + 1} serial`} placeholder="Serial" value={r.serialNumber} onChange={(e) => set(i, 'serialNumber', e.target.value)} />
          <select className={WS_INPUT} aria-label={`Joint ${i + 1} condition`} value={r.condition} onChange={(e) => set(i, 'condition', e.target.value)}>
            <option value="">Condition</option>
            {opts(catalog?.stringConditions)}
          </select>
          <button type="button" className={WS_SECONDARY} aria-label={`Remove joint ${i + 1}`} onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            <Trash2 className="size-4" aria-hidden="true" />
          </button>
        </div>
      ))}
      <button type="button" className={WS_SECONDARY} onClick={() => onChange([...rows, blankLine()])}>
        <Plus className="size-4" aria-hidden="true" />
        Add joint
      </button>
    </div>
  );
}

/** The API's totals, as sent. Nothing here adds the lines up. */
export function TallyTotals({ tally, conditions }: { tally: Pick<WsTally, 'totals' | 'lengthUnit'>; conditions?: { value: string; label: string }[] }) {
  const t = tally.totals;
  const unit = tally.lengthUnit;
  const byCondition = Object.entries(t.byCondition ?? {});
  return (
    <dl className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="ws-tally-totals">
      <WsSpec label="Joints" value={fmtWsNumber(t.joints)} mono />
      <WsSpec label="Total" value={withUnit(t.totalLength, unit)} mono />
      <WsSpec label="Total in metres" value={withUnit(t.totalLengthM, 'm')} mono />
      <WsSpec label="Average" value={withUnit(t.averageLength, unit)} mono />
      <WsSpec label="Shortest" value={withUnit(t.shortest, unit)} mono />
      <WsSpec label="Longest" value={withUnit(t.longest, unit)} mono />
      {byCondition.length > 0 && (
        <WsSpec label="By condition" value={byCondition.map(([k, n]) => `${catalogLabel(conditions, k)} ${fmtWsNumber(n)}`).join(' · ')} />
      )}
    </dl>
  );
}

interface TallyHeaderForm {
  tourId: string;
  tallyKind: string;
  direction: string;
  description: string;
  size: string;
  grade: string;
  lengthUnit: string;
  notes: string;
}

function TallyCard({ tally, catalog, role }: { tally: WsTally; catalog: WsCatalog | null; role: string | null | undefined }) {
  const update = useUpdateWsTally();
  const remove = useRemoveWsTally();
  const voidTally = useVoidWsTally();
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<WsTallyLineForm[]>([]);
  const [header, setHeader] = useState<TallyHeaderForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const editable = !tally.locked && canWriteWsTours(role);
  const voidable = !tally.voided && canApproveWsTours(role);
  const depthUnits = catalog?.depthUnits?.length ? catalog.depthUnits : ['m', 'ft'];
  const title = `${tally.tallyKindLabel ?? catalogLabel(catalog?.tallyKinds, tally.tallyKind)} · ${tally.directionLabel ?? catalogLabel(catalog?.tallyDirections, tally.direction)}`;

  const startEdit = () => {
    setError(null);
    setRows(
      tally.lines.length
        ? tally.lines.map((l) => ({ length: String(l.lengthEntered), serialNumber: l.serialNumber ?? '', condition: l.condition ?? '' }))
        : [blankLine()],
    );
    setHeader({
      tourId: tally.tourId, tallyKind: tally.tallyKind, direction: tally.direction, description: tally.description ?? '',
      size: tally.size ?? '', grade: tally.grade ?? '', lengthUnit: tally.lengthUnit, notes: tally.notes ?? '',
    });
    setEditing(true);
  };

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!header) return;
    setError(null);
    const built = wsTallyLinesBody(rows);
    if ('error' in built) return setError(built.error);
    const txt = (s: string) => (s.trim() === '' ? null : s.trim());
    // One PATCH carries the header and the joints: a unit change re-reads every joint, so they travel together.
    update.mutate(
      {
        id: tally.id,
        body: {
          tally_kind: header.tallyKind, direction: header.direction, description: txt(header.description), size: txt(header.size),
          grade: txt(header.grade), length_unit: header.lengthUnit, notes: txt(header.notes), lines: built.lines,
        },
      },
      {
        onSuccess: () => {
          toast.success('Tally saved');
          setEditing(false);
        },
        onError: (err) => setError(wsJobRefusal(err, 'The tally could not be saved.')),
      },
    );
  };

  return (
    <article
      className={`rounded-fi-control border border-fieldiq-border border-l-4 bg-fieldiq-card ${tally.voided ? WS_VOIDED_SKIN.rail : 'border-l-transparent'}`}
      data-testid="ws-tally"
      data-locked={tally.locked ? 'true' : 'false'}
    >
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-fieldiq-border px-4 py-3">
        <div>
          <p className={`text-sm font-extrabold ${tally.voided ? 'text-fieldiq-muted line-through' : 'text-white'}`}>{title}</p>
          <p className="mt-0.5 text-xs text-fieldiq-text">
            {wsDay(tally.workDate)} · {wsItemIdentity(tally)} · Entered in {tally.lengthUnit}
          </p>
          {tally.voided && (
            <p className="mt-0.5 text-xs text-fi-red">
              Voided{tally.voidedByName ? ` by ${tally.voidedByName}` : ''}: {tally.voidReason || '—'}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <WsBadge skin={wsTourStatusSkin(tally.tourStatus)} value={tally.tourStatus} />
          {tally.voided && <WsBadge skin={WS_VOIDED_SKIN} value="voided" />}
          {editable && !editing && (
            <>
              <button type="button" className={WS_SECONDARY} onClick={startEdit} aria-label="Edit tally">
                <Pencil className="size-4" aria-hidden="true" />
                Edit
              </button>
              <button type="button" className={WS_SECONDARY} onClick={() => { setRemoveError(null); setRemoving(true); }} aria-label="Delete tally">
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </>
          )}
          {voidable && (
            <button type="button" className={WS_SECONDARY} onClick={() => { setVoidError(null); setVoiding(true); }} aria-label="Void tally">
              <Ban className="size-4" aria-hidden="true" />
              Void
            </button>
          )}
        </div>
      </div>
      <div className="px-4 py-3">
        <TallyTotals tally={tally} conditions={catalog?.stringConditions} />
      </div>
      {editing && header ? (
        <form onSubmit={save} className="space-y-3 border-t border-fieldiq-border px-4 py-4" data-testid="ws-tally-edit">
          <WsFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <WsField label="Kind">
              <select className={WS_INPUT} aria-label="Tally kind" value={header.tallyKind} onChange={(e) => setHeader({ ...header, tallyKind: e.target.value })}>
                {opts(catalog?.tallyKinds)}
              </select>
            </WsField>
            <WsField label="Direction">
              <select className={WS_INPUT} aria-label="Tally direction" value={header.direction} onChange={(e) => setHeader({ ...header, direction: e.target.value })}>
                {opts(catalog?.tallyDirections)}
              </select>
            </WsField>
            <WsField label="Length unit" hint="Changing it re-reads every joint below">
              <select className={WS_INPUT} aria-label="Tally length unit" value={header.lengthUnit} onChange={(e) => setHeader({ ...header, lengthUnit: e.target.value })}>
                {depthUnits.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </WsField>
            <WsField label="Description">
              <input className={WS_INPUT} aria-label="Tally description" value={header.description} onChange={(e) => setHeader({ ...header, description: e.target.value })} />
            </WsField>
            <WsField label="Size">
              <input className={WS_INPUT} aria-label="Tally size" value={header.size} onChange={(e) => setHeader({ ...header, size: e.target.value })} />
            </WsField>
            <WsField label="Grade">
              <input className={WS_INPUT} aria-label="Tally grade" value={header.grade} onChange={(e) => setHeader({ ...header, grade: e.target.value })} />
            </WsField>
          </div>
          <TallyLinesEditor rows={rows} onChange={setRows} catalog={catalog} unit={header.lengthUnit} />
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className={WS_SECONDARY} onClick={() => setEditing(false)} disabled={update.isPending}>
              Cancel
            </button>
            <button type="submit" className={WS_PRIMARY} disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save tally'}
            </button>
          </div>
        </form>
      ) : tally.lines.length === 0 ? (
        <div className="border-t border-fieldiq-border px-4 py-3">
          <WsEmpty>No joints on this tally yet.</WsEmpty>
        </div>
      ) : (
        <div className="overflow-x-auto border-t border-fieldiq-border">
          <table className="w-full min-w-[520px] text-left text-sm" data-testid="ws-tally-lines">
            <thead className="bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th className="px-4 py-2 text-right">Joint</th>
                <th className="px-4 py-2 text-right">Length ({tally.lengthUnit})</th>
                <th className="px-4 py-2">Serial</th>
                <th className="px-4 py-2">Condition</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-fieldiq-border text-fieldiq-text">
              {tally.lines.map((l) => (
                <tr key={l.id}>
                  <td className="px-4 py-1.5 text-right font-mono">{l.lineNo}</td>
                  <td className="px-4 py-1.5 text-right font-mono">{fmtWsNumber(l.lengthEntered)}</td>
                  <td className="px-4 py-1.5 font-mono">{l.serialNumber || '—'}</td>
                  <td className="px-4 py-1.5">{l.conditionLabel ?? (l.condition ? catalogLabel(catalog?.stringConditions, l.condition) : '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Confirm
        open={removing}
        title="Delete tally"
        body="Deletes this tally and its joints from the draft sheet."
        confirmLabel="Delete"
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(false)}
        onConfirm={() =>
          remove.mutate(tally.id, {
            onSuccess: () => {
              toast.success('Tally deleted');
              setRemoving(false);
            },
            onError: (err) => setRemoveError(wsJobRefusal(err, 'The tally could not be deleted.')),
          })
        }
      />
      <VoidDialog
        open={voiding}
        title="Void tally"
        body="Voids this tally. It stays on the record, struck through, with your reason."
        pending={voidTally.isPending}
        error={voidError}
        onClose={() => setVoiding(false)}
        onVoid={(reason) =>
          voidTally.mutate(
            { id: tally.id, reason },
            {
              onSuccess: () => {
                toast.success('Tally voided');
                setVoiding(false);
              },
              onError: (err) => setVoidError(wsJobRefusal(err, 'The tally could not be voided.')),
            },
          )
        }
      />
    </article>
  );
}

export function TalliesSection({
  job, catalog, depthUnit, role,
}: {
  job: WsJobDetail;
  catalog: WsCatalog | null;
  depthUnit: string;
  role: string | null | undefined;
}) {
  const query = useWsTallies(job.id);
  const tours = useWsTours(job.id);
  const create = useCreateWsTally();
  const tallies = query.data?.data ?? [];
  const draftSheets = (tours.data?.data ?? []).filter((t) => t.status === 'draft');
  const canCreate = canWriteWsTours(role) && draftSheets.length > 0;
  const depthUnits = catalog?.depthUnits?.length ? catalog.depthUnits : ['m', 'ft'];
  const blank: TallyHeaderForm = {
    tourId: '', tallyKind: catalog?.tallyKinds?.[0]?.value ?? 'tubing', direction: catalog?.tallyDirections?.[0]?.value ?? 'pulled',
    description: '', size: '', grade: '', lengthUnit: depthUnit, notes: '',
  };
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<TallyHeaderForm>(blank);
  const [rows, setRows] = useState<WsTallyLineForm[]>([blankLine()]);
  const [error, setError] = useState<string | null>(null);
  const failure = queryLoadFailure(query);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.tourId) return setError('Choose the tour sheet.');
    const built = wsTallyLinesBody(rows);
    if ('error' in built) return setError(built.error);
    const txt = (s: string) => (s.trim() === '' ? null : s.trim());
    create.mutate(
      {
        tourId: form.tourId,
        body: {
          tally_kind: form.tallyKind, direction: form.direction, description: txt(form.description), size: txt(form.size),
          grade: txt(form.grade), length_unit: form.lengthUnit, notes: txt(form.notes), lines: built.lines,
        },
      },
      {
        onSuccess: () => {
          toast.success('Tally added');
          setAdding(false);
        },
        onError: (err) => setError(wsJobRefusal(err, 'The tally could not be added.')),
      },
    );
  };

  return (
    <>
      <WsPanel
        eyebrow="String"
        title="Tallies"
        testId="ws-job-tallies"
        actions={
          canCreate && !adding ? (
            <button
              type="button"
              className={WS_PRIMARY}
              data-testid="ws-new-tally"
              onClick={() => {
                setError(null);
                setForm({ ...blank, tourId: draftSheets.length === 1 ? draftSheets[0].id : '' });
                setRows([blankLine()]);
                setAdding(true);
              }}
            >
              <Plus className="size-4" aria-hidden="true" />
              New tally
            </button>
          ) : undefined
        }
      >
        <p className="border-b border-fieldiq-border px-4 py-3 text-sm text-fieldiq-muted">
          A joint-by-joint count on a tour sheet. The totals are worked out by FieldIQ from the joints. A tally changes only while its sheet is
          a draft; after that the office can void it with a reason.
        </p>
        {adding && (
          <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="ws-tally-form">
            <WsFormError message={error} />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <WsField label="Tour sheet" hint="Draft sheets only">
                <select className={WS_INPUT} aria-label="Tally tour sheet" value={form.tourId} onChange={(e) => setForm({ ...form, tourId: e.target.value })}>
                  <option value="">Choose…</option>
                  {draftSheets.map((t) => (
                    <option key={t.id} value={t.id}>
                      {wsDay(t.workDate)}
                    </option>
                  ))}
                </select>
              </WsField>
              <WsField label="Kind">
                <select className={WS_INPUT} aria-label="Tally kind" value={form.tallyKind} onChange={(e) => setForm({ ...form, tallyKind: e.target.value })}>
                  {opts(catalog?.tallyKinds)}
                </select>
              </WsField>
              <WsField label="Direction">
                <select className={WS_INPUT} aria-label="Tally direction" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
                  {opts(catalog?.tallyDirections)}
                </select>
              </WsField>
              <WsField label="Length unit">
                <select className={WS_INPUT} aria-label="Tally length unit" value={form.lengthUnit} onChange={(e) => setForm({ ...form, lengthUnit: e.target.value })}>
                  {depthUnits.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </WsField>
              <WsField label="Description">
                <input className={WS_INPUT} aria-label="Tally description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </WsField>
              <WsField label="Size">
                <input className={WS_INPUT} aria-label="Tally size" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} />
              </WsField>
              <WsField label="Grade">
                <input className={WS_INPUT} aria-label="Tally grade" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} />
              </WsField>
            </div>
            <TallyLinesEditor rows={rows} onChange={setRows} catalog={catalog} unit={form.lengthUnit} />
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className={WS_SECONDARY} onClick={() => setAdding(false)} disabled={create.isPending}>
                Cancel
              </button>
              <button type="submit" className={WS_PRIMARY} disabled={create.isPending}>
                {create.isPending ? 'Saving…' : 'Add tally'}
              </button>
            </div>
          </form>
        )}
        <div className="px-4 py-4">
          {query.isLoading ? (
            <WsLoading label="Loading the tallies" />
          ) : failure ? (
            <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the tallies" />
          ) : tallies.length === 0 ? (
            <WsEmpty>No tally has been recorded on this job.</WsEmpty>
          ) : (
            <p className="text-sm text-fieldiq-muted">
              {tallies.length} {tallies.length === 1 ? 'tally' : 'tallies'}
            </p>
          )}
        </div>
      </WsPanel>
      {tallies.map((t) => (
        <TallyCard key={t.id} tally={t} catalog={catalog} role={role} />
      ))}
    </>
  );
}
